import React, { useEffect, useRef, useState } from 'react';
import AdminLayout from '../layouts/AdminLayout';
import { templateService, Template } from '../../services/api';
import './Impostazioni.css';

const Impostazioni: React.FC = () => {
  const [editTemplate1, setEditTemplate1] = useState(false);
  const [editTemplate2, setEditTemplate2] = useState(false);
  const [template1, setTemplate1] = useState<Template | null>(null);
  const [template2, setTemplate2] = useState<Template | null>(null);
  const [backup1, setBackup1] = useState<Template | null>(null);
  const [backup2, setBackup2] = useState<Template | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templatesError, setTemplatesError] = useState('');
  const [saving1, setSaving1] = useState(false);
  const [saving2, setSaving2] = useState(false);
  const [uploadingLogo1, setUploadingLogo1] = useState(false);
  const [uploadingBgData2, setUploadingBgData2] = useState(false);
  const [uploadingBgLogo2, setUploadingBgLogo2] = useState(false);
  const [uploadError, setUploadError] = useState('');
  /** Testo email condiviso per entrambi i template */
  const [emailBodyTemplate, setEmailBodyTemplate] = useState('');
  /** Oggetto email condiviso per entrambi i template */
  const [emailSubject, setEmailSubject] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  const fileLogoRef1 = useRef<HTMLInputElement>(null);
  const fileBgDataRef2 = useRef<HTMLInputElement>(null);
  const fileBgLogoRef2 = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingTemplates(true);
        const data = await templateService.getTemplates();
        const t1 = data.find((t) => t.slug === 'template_1') || null;
        const t2 = data.find((t) => t.slug === 'template_2') || null;
        setTemplate1(t1);
        setTemplate2(t2);
        const body = t1?.email_body_template ?? t2?.email_body_template ?? '';
        setEmailBodyTemplate(body);
        const subj = t1?.email_subject ?? t2?.email_subject ?? '🎁 Estetica Cruciani';
        setEmailSubject(subj);
      } catch (err: any) {
        setTemplatesError(err.response?.data?.message || 'Errore nel caricamento dei template');
      } finally {
        setLoadingTemplates(false);
      }
    };
    load();
  }, []);

  const handleTemplate1Field = (field: keyof Template, value: string) => {
    setTemplate1((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleTemplate2Field = (field: keyof Template, value: string) => {
    setTemplate2((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const normalizeHex = (value: string, fallback: string) => {
    const trimmed = value?.trim();
    if (!trimmed) return fallback;
    const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    return /^#[0-9A-Fa-f]{6}$/.test(withHash) ? withHash : fallback;
  };

  const handleEditTemplate1 = () => {
    if (template1) setBackup1({ ...template1 });
    setEditTemplate1(true);
  };

  const handleEditTemplate2 = () => {
    if (template2) setBackup2({ ...template2 });
    setEditTemplate2(true);
  };

  const handleCancelTemplate1 = () => {
    if (backup1) setTemplate1(backup1);
    setEditTemplate1(false);
  };

  const handleCancelTemplate2 = () => {
    if (backup2) setTemplate2(backup2);
    setEditTemplate2(false);
  };

  const handleSaveTemplate1 = async () => {
    if (!template1) return;
    setSaving1(true);
    try {
      const { template } = await templateService.updateTemplate(template1.id, {
        title_data: template1.title_data,
        subtitle: template1.subtitle,
        address: template1.address,
        website: template1.website,
        phone: template1.phone,
        image_logo: template1.image_logo,
        text_color_components: template1.text_color_components,
        text_color_data: template1.text_color_data,
      });
      setTemplate1(template);
      setEditTemplate1(false);
      setBackup1(null);
    } catch (err: any) {
      setTemplatesError(err.response?.data?.message || 'Errore nel salvataggio del template');
    } finally {
      setSaving1(false);
    }
  };

  const handleSaveEmailBody = async () => {
    if (!template1 || !template2) return;
    setSavingEmail(true);
    setTemplatesError('');
    try {
      await templateService.updateTemplate(template1.id, {
        email_body_template: emailBodyTemplate || undefined,
        email_subject: emailSubject.trim() || undefined,
      });
      await templateService.updateTemplate(template2.id, {
        email_body_template: emailBodyTemplate || undefined,
        email_subject: emailSubject.trim() || undefined,
      });
      const data = await templateService.getTemplates();
      setTemplate1(data.find((t) => t.slug === 'template_1') || null);
      setTemplate2(data.find((t) => t.slug === 'template_2') || null);
    } catch (err: any) {
      setTemplatesError(err.response?.data?.message || 'Errore nel salvataggio del testo email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSaveTemplate2 = async () => {
    if (!template2) return;
    setSaving2(true);
    try {
      const { template } = await templateService.updateTemplate(template2.id, {
        title: template2.title,
        phone: template2.phone,
        address: template2.address,
        website: template2.website,
        text_color_components: template2.text_color_components,
        text_color_data: template2.text_color_data,
        bg_data_image: template2.bg_data_image,
        bg_logo_image: template2.bg_logo_image,
      });
      setTemplate2(template);
      setEditTemplate2(false);
      setBackup2(null);
    } catch (err: any) {
      setTemplatesError(err.response?.data?.message || 'Errore nel salvataggio del template');
    } finally {
      setSaving2(false);
    }
  };

  return (
    <AdminLayout activeSection="impostazioni">
      <div className="template-page">
        <h1 className="template-title">Template</h1>

        {loadingTemplates && <div className="template-loading">Caricamento...</div>}
        {templatesError && <div className="template-error">{templatesError}</div>}

        <div className="template-sections">
          {/* Template 1 - Nuovo */}
          <div className="template-card">
            <div className="template-card-header">
              <h3 className={editTemplate1 ? 'template-card-title edit' : 'template-card-title'}>Template 1 - Nuovo</h3>
              {!editTemplate1 && (
                <button
                  className="template-edit-btn"
                  type="button"
                  onClick={handleEditTemplate1}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 2H3.33333C2.97971 2 2.64057 2.14048 2.39052 2.39052C2.14048 2.64057 2 2.97971 2 3.33333V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H12.6667C13.0203 14 13.3594 13.8595 13.6095 13.6095C13.8595 13.3594 14 13.0203 14 12.6667V8" stroke="#E60076" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12.2504 1.75015C12.5156 1.48493 12.8753 1.33594 13.2504 1.33594C13.6255 1.33594 13.9852 1.48493 14.2504 1.75015C14.5156 2.01537 14.6646 2.37508 14.6646 2.75015C14.6646 3.12522 14.5156 3.48493 14.2504 3.75015L8.24172 9.75948C8.08342 9.91765 7.88786 10.0334 7.67305 10.0962L5.75772 10.6562C5.70036 10.6729 5.63955 10.6739 5.58166 10.6591C5.52377 10.6442 5.47094 10.6141 5.42869 10.5719C5.38643 10.5296 5.35631 10.4768 5.34148 10.4189C5.32665 10.361 5.32766 10.3002 5.34439 10.2428L5.90439 8.32748C5.96741 8.11285 6.08341 7.91752 6.24172 7.75948L12.2504 1.75015Z" stroke="#E60076" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Modifica
                </button>
              )}
            </div>

            <div className="template-card-body">
              <div className="template-row template-row-3">
                <div className="template-field">
                  <label>Titolo Lato Dati (max 15 caratteri)</label>
                  <input
                    className="template-input"
                    type="text"
                    placeholder="Buono Regalo"
                    disabled={!editTemplate1}
                    value={template1?.title_data ?? ''}
                    onChange={(e) => handleTemplate1Field('title_data', e.target.value)}
                    maxLength={15}
                  />
                </div>
                <div className="template-field">
                  <label>Sottotitolo (max 80 caratteri)</label>
                  <input
                    className="template-input"
                    type="text"
                    placeholder="Un pensiero speciale che diventa un’esperienza unica"
                    disabled={!editTemplate1}
                    value={template1?.subtitle ?? ''}
                    onChange={(e) => handleTemplate1Field('subtitle', e.target.value)}
                  />
                </div>
                <div className="template-field">
                  <label>Immagine Lato Logo</label>
                  <button
                    type="button"
                    className="template-upload"
                    disabled={!editTemplate1}
                    onClick={() => fileLogoRef1.current?.click()}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="#F7349B" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M11.3337 5.33333L8.00033 2L4.66699 5.33333" stroke="#F7349B" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 2V10" stroke="#F7349B" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {uploadingLogo1 ? 'Caricamento...' : template1?.image_logo ? template1.image_logo : 'Carica un’immagine'}
                  </button>
                  <input
                    ref={fileLogoRef1}
                    type="file"
                    accept="image/*"
                    className="template-file-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file) return;
                      setUploadError('');
                      setUploadingLogo1(true);
                      try {
                        const { filename } = await templateService.uploadImage(file);
                        handleTemplate1Field('image_logo', filename);
                      } catch (err: any) {
                        setUploadError(err.response?.data?.message || 'Errore nel caricamento dell’immagine');
                      } finally {
                        setUploadingLogo1(false);
                      }
                    }}
                  />
                  {uploadingLogo1 && <span className="template-upload-status">Caricamento...</span>}
                  {uploadError && <span className="template-upload-error">{uploadError}</span>}
                </div>
              </div>

              <div className="template-row template-row-3">
                <div className="template-field">
                  <label>Indirizzo</label>
                  <input
                    className="template-input"
                    type="text"
                    placeholder="Via A. Albricci 9, MI"
                    disabled={!editTemplate1}
                    value={template1?.address ?? ''}
                    onChange={(e) => handleTemplate1Field('address', e.target.value)}
                  />
                </div>
                <div className="template-field">
                  <label>Sito web</label>
                  <input
                    className="template-input"
                    type="text"
                    placeholder="esteticacruciani.it"
                    disabled={!editTemplate1}
                    value={template1?.website ?? ''}
                    onChange={(e) => handleTemplate1Field('website', e.target.value)}
                  />
                </div>
                <div className="template-field">
                  <label>Numero di telefono</label>
                  <input
                    className="template-input"
                    type="text"
                    placeholder="+39 351 811 7814"
                    disabled={!editTemplate1}
                    value={template1?.phone ?? ''}
                    onChange={(e) => handleTemplate1Field('phone', e.target.value)}
                  />
                </div>
              </div>

              <div className="template-row template-row-2">
                <div className="template-field">
                  <label>Colore Testo Componenti</label>
                  <input
                    className="template-color-input"
                    type="color"
                    disabled={!editTemplate1}
                    value={normalizeHex(template1?.text_color_components ?? '', '#752c28')}
                    onChange={(e) => handleTemplate1Field('text_color_components', e.target.value)}
                    style={{ backgroundColor: normalizeHex(template1?.text_color_components ?? '', '#752c28') }}
                  />
                </div>
                <div className="template-field">
                  <label>Colore Testo Dati che inserisci</label>
                  <input
                    className="template-color-input"
                    type="color"
                    disabled={!editTemplate1}
                    value={normalizeHex(template1?.text_color_data ?? '', '#752c28')}
                    onChange={(e) => handleTemplate1Field('text_color_data', e.target.value)}
                    style={{ backgroundColor: normalizeHex(template1?.text_color_data ?? '', '#752c28') }}
                  />
                </div>
              </div>
            </div>
            {editTemplate1 && (
              <div className="template-card-actions">
                <button type="button" className="template-cancel-btn" onClick={handleCancelTemplate1}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M4 4L12 12" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Annulla
                </button>
                <button type="button" className="template-save-btn" onClick={handleSaveTemplate1} disabled={saving1}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3v7M4.5 6.5L8 3l3.5 3.5" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 10.5a2.5 2.5 0 0 0 2.5 2.5h5A2.5 2.5 0 0 0 13 10.5" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {saving1 ? 'Salvataggio...' : 'Salva Modifiche'}
                </button>
              </div>
            )}
          </div>

          {/* Template 2 - Vecchio */}
          <div className="template-card">
            <div className="template-card-header">
              <h3 className={editTemplate2 ? 'template-card-title edit' : 'template-card-title'}>Template 2 - Vecchio</h3>
              {!editTemplate2 && (
                <button
                  className="template-edit-btn"
                  type="button"
                  onClick={handleEditTemplate2}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 2H3.33333C2.97971 2 2.64057 2.14048 2.39052 2.39052C2.14048 2.64057 2 2.97971 2 3.33333V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H12.6667C13.0203 14 13.3594 13.8595 13.6095 13.6095C13.8595 13.3594 14 13.0203 14 12.6667V8" stroke="#E60076" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12.2504 1.75015C12.5156 1.48493 12.8753 1.33594 13.2504 1.33594C13.6255 1.33594 13.9852 1.48493 14.2504 1.75015C14.5156 2.01537 14.6646 2.37508 14.6646 2.75015C14.6646 3.12522 14.5156 3.48493 14.2504 3.75015L8.24172 9.75948C8.08342 9.91765 7.88786 10.0334 7.67305 10.0962L5.75772 10.6562C5.70036 10.6729 5.63955 10.6739 5.58166 10.6591C5.52377 10.6442 5.47094 10.6141 5.42869 10.5719C5.38643 10.5296 5.35631 10.4768 5.34148 10.4189C5.32665 10.361 5.32766 10.3002 5.34439 10.2428L5.90439 8.32748C5.96741 8.11285 6.08341 7.91752 6.24172 7.75948L12.2504 1.75015Z" stroke="#E60076" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Modifica
                </button>
              )}
            </div>

            <div className="template-card-body">
              <div className="template-row template-row-3">
                <div className="template-group">
                  <div className="template-field">
                    <label>Titolo</label>
                    <input
                      className="template-input"
                      type="text"
                      placeholder="RICEVO SU APPUNTAMENTO"
                      disabled={!editTemplate2}
                      value={template2?.title ?? ''}
                      onChange={(e) => handleTemplate2Field('title', e.target.value)}
                    />
                  </div>
                  <div className="template-field">
                    <label>Indirizzo</label>
                    <input
                      className="template-input"
                      type="text"
                      placeholder="Via Alberico Albricci, 9 M. (Missori)"
                      disabled={!editTemplate2}
                      value={template2?.address ?? ''}
                      onChange={(e) => handleTemplate2Field('address', e.target.value)}
                    />
                  </div>
                </div>

                <div className="template-group">
                  <div className="template-field">
                    <label>Telefono</label>
                    <input
                      className="template-input"
                      type="text"
                      placeholder="+39 3511117814"
                      disabled={!editTemplate2}
                      value={template2?.phone ?? ''}
                      onChange={(e) => handleTemplate2Field('phone', e.target.value)}
                    />
                  </div>
                  <div className="template-field">
                    <label>Sito web</label>
                    <input
                      className="template-input"
                      type="text"
                      placeholder="esteticacruciani.it"
                      disabled={!editTemplate2}
                      value={template2?.website ?? ''}
                      onChange={(e) => handleTemplate2Field('website', e.target.value)}
                    />
                  </div>
                </div>

                <div className="template-group">
                  <div className="template-row template-row-2">
                    <div className="template-field">
                      <label>Colore Testo Componenti</label>
                      <input
                        className="template-color-input"
                        type="color"
                        disabled={!editTemplate2}
                        value={normalizeHex(template2?.text_color_components || '', '#FEF9F9')}
                        onChange={(e) => handleTemplate2Field('text_color_components', e.target.value)}
                        style={{ backgroundColor: normalizeHex(template2?.text_color_components || '', '#FEF9F9') }}
                      />
                    </div>
                    <div className="template-field">
                      <label>Colore Testo Dati che inserisci</label>
                      <input
                        className="template-color-input"
                        type="color"
                        disabled={!editTemplate2}
                        value={normalizeHex(template2?.text_color_data || '', '#B9756B')}
                        onChange={(e) => handleTemplate2Field('text_color_data', e.target.value)}
                        style={{ backgroundColor: normalizeHex(template2?.text_color_data || '', '#B9756B') }}
                      />
                    </div>
                  </div>

                  <div className="template-row template-row-2">
                    <div className="template-field">
                      <label>Sfondo Lato Dati</label>
                      <button
                        type="button"
                        className="template-upload"
                        disabled={!editTemplate2}
                        onClick={() => fileBgDataRef2.current?.click()}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="#F7349B" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M11.3337 5.33333L8.00033 2L4.66699 5.33333" stroke="#F7349B" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 2V10" stroke="#F7349B" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {uploadingBgData2 ? 'Caricamento...' : template2?.bg_data_image ? template2.bg_data_image : 'Carica un’immagine'}
                      </button>
                      <input
                        ref={fileBgDataRef2}
                        type="file"
                        accept="image/*"
                        className="template-file-input"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file) return;
                          setUploadError('');
                          setUploadingBgData2(true);
                          try {
                            const { filename } = await templateService.uploadImage(file);
                            handleTemplate2Field('bg_data_image', filename);
                          } catch (err: any) {
                            setUploadError(err.response?.data?.message || 'Errore nel caricamento dell’immagine');
                          } finally {
                            setUploadingBgData2(false);
                          }
                        }}
                      />
                    </div>
                    <div className="template-field">
                      <label>Sfondo Lato Logo</label>
                      <button
                        type="button"
                        className="template-upload"
                        disabled={!editTemplate2}
                        onClick={() => fileBgLogoRef2.current?.click()}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="#F7349B" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M11.3337 5.33333L8.00033 2L4.66699 5.33333" stroke="#F7349B" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 2V10" stroke="#F7349B" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {uploadingBgLogo2 ? 'Caricamento...' : template2?.bg_logo_image ? template2.bg_logo_image : 'Carica un’immagine'}
                      </button>
                      <input
                        ref={fileBgLogoRef2}
                        type="file"
                        accept="image/*"
                        className="template-file-input"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file) return;
                          setUploadError('');
                          setUploadingBgLogo2(true);
                          try {
                            const { filename } = await templateService.uploadImage(file);
                            handleTemplate2Field('bg_logo_image', filename);
                          } catch (err: any) {
                            setUploadError(err.response?.data?.message || 'Errore nel caricamento dell’immagine');
                          } finally {
                            setUploadingBgLogo2(false);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {editTemplate2 && (
              <div className="template-card-actions">
                <button type="button" className="template-cancel-btn" onClick={handleCancelTemplate2}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M4 4L12 12" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Annulla
                </button>
                <button type="button" className="template-save-btn" onClick={handleSaveTemplate2} disabled={saving2}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3v7M4.5 6.5L8 3l3.5 3.5" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 10.5a2.5 2.5 0 0 0 2.5 2.5h5A2.5 2.5 0 0 0 13 10.5" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {saving2 ? 'Salvataggio...' : 'Salva Modifiche'}
                </button>
              </div>
            )}
          </div>

          {/* Oggetto e testo email unici per entrambi i template */}
          <div className="template-email-section">
            <h3 className="template-email-section-title">Email invio buoni</h3>
            <div className="template-field">
              <label htmlFor="template-email-subject">Oggetto email</label>
              <input
                id="template-email-subject"
                type="text"
                className="template-input template-input-email-subject"
                placeholder="🎁 Estetica Cruciani"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>
            <div className="template-field">
              <label htmlFor="template-email-body">Testo messaggio</label>
              <p className="template-email-placeholders">
                Codici: <code>[a]</code> Per · <code>[da]</code> Da · <code>[regalo]</code> Regalo · <code>[scadenza]</code> Scadenza · <code>[numero_buono]</code> Numero buono
              </p>
              <textarea
                id="template-email-body"
                className="template-textarea-email"
              placeholder="Ciao [a], hai ricevuto un buono regalo &quot;[regalo]&quot; da [da]..."
              value={emailBodyTemplate}
                onChange={(e) => setEmailBodyTemplate(e.target.value)}
                rows={6}
              />
            </div>
            <button
              type="button"
              className="template-save-email-btn"
              onClick={handleSaveEmailBody}
              disabled={savingEmail || loadingTemplates}
            >
              {savingEmail ? 'Salvataggio...' : 'Salva oggetto e testo email'}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Impostazioni;
